import { useEffect, useState } from 'react';
import { BRAND_LOGO } from './brand';
import { useStore, selectActiveRom, hydrateSettings } from './state/store';
import { LibraryScreen } from './ui/LibraryScreen';
import { PlayerScreen } from './ui/PlayerScreen';
import { SettingsScreen } from './ui/SettingsScreen';
import { MyScreen } from './ui/MyScreen';
import { LoginScreen } from './ui/LoginScreen';
import { HomeScreen } from './ui/HomeScreen';
import { ComingSoonScreen } from './ui/ComingSoonScreen';
import { GamesScreen } from './games/GamesScreen';
import { AiNovelPlayerScreen } from './games/ai-novel/AiNovelPlayerScreen';
import { useNovelStore } from './games/ai-novel/novelStore';
import { AppShell } from './ui/AppShell';
import { isHubView } from './ui/appNav';
import { Toasts } from './ui/Toasts';
import { LoadProgressOverlay } from './ui/LoadProgressOverlay';
import { setupPwa } from './pwa/register';
import { requestPersistence } from './storage/db';
import { getCurrentUser, startAutoSync } from './storage/cloudSync';

/** iOS のノッチ／ステータスバー下に不透明帯を敷き、システム側のフロストを防ぐ。 */
function StatusBarShield({ color = '#07090f' }: { color?: string }) {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-[80]"
      style={{
        height: 'calc(var(--safe-top) + var(--portrait-top-gap))',
        backgroundColor: color,
      }}
    />
  );
}

export default function App() {
  const view = useStore((s) => s.view);
  const activeRom = useStore(selectActiveRom);
  const exitPlayer = useStore((s) => s.exitPlayer);
  const playGeneration = useStore((s) => s.playGeneration);
  const setView = useStore((s) => s.setView);
  const toast = useStore((s) => s.toast);
  const refreshConflicts = useStore((s) => s.refreshConflicts);
  const refreshRoms = useStore((s) => s.refreshRoms);
  const sessionUser = useStore((s) => s.sessionUser);
  const sessionReady = useStore((s) => s.sessionReady);
  const setSessionUser = useStore((s) => s.setSessionUser);
  const setSessionReady = useStore((s) => s.setSessionReady);

  const [updateFn, setUpdateFn] = useState<(() => Promise<void>) | null>(null);
  const [bootPercent, setBootPercent] = useState(8);
  const [bootLabel, setBootLabel] = useState('準備中');
  const [settingsReady, setSettingsReady] = useState(false);
  const novelPhase = useNovelStore((s) => s.phase);
  const playingNovel = view === 'games' && novelPhase === 'play';

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setBootPercent(12);
      setBootLabel('設定を読み出し中');
      await hydrateSettings();
      if (cancelled) return;
      setSettingsReady(true);
      setBootPercent(55);
      setBootLabel('アカウントを確認中');
      try {
        const u = await getCurrentUser();
        if (!cancelled) setSessionUser(u);
      } finally {
        if (!cancelled) {
          setBootPercent(100);
          setBootLabel('準備完了');
          setSessionReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setSessionUser, setSessionReady]);

  // ログイン前から SW を張り、更新バナーを出せるようにする
  useEffect(() => {
    setupPwa({
      onNeedRefresh: (update) => setUpdateFn(() => update),
      onOfflineReady: () => toast('オフラインで使えるようになりました', 'success'),
    });
  }, [toast]);

  useEffect(() => {
    if (!sessionUser) return;

    void requestPersistence();
    void refreshConflicts();
    void refreshRoms();

    const stopSync = startAutoSync(undefined, (result) => {
      if (result.libraryImported > 0 || result.downloaded > 0 || result.conflictsAutoResolved > 0) {
        void refreshRoms();
      }
      if (result.conflicts > 0 || result.conflictsAutoResolved > 0) {
        void refreshConflicts();
      }
      if (result.conflicts > 0) {
        toast(`同期で ${result.conflicts} 件の競合が見つかりました。My で確認してください。`, 'error');
      } else if (result.downloaded > 0 || result.libraryImported > 0) {
        const parts: string[] = [];
        if (result.libraryImported) parts.push(`ライブラリ ${result.libraryImported} 件`);
        if (result.downloaded) {
          const labels = result.downloadedLabels.slice(0, 3);
          const more =
            result.downloadedLabels.length > 3
              ? `ほか ${result.downloadedLabels.length - 3} 件`
              : '';
          if (labels.length > 0) {
            parts.push(more ? `${labels.join('、')}、${more}` : labels.join('、'));
          } else {
            parts.push(`セーブ ${result.downloaded} 件`);
          }
        }
        toast(`${parts.join('、')}を取り込みました`, 'success');
      }
    });

    const params = new URLSearchParams(location.search);
    const deep = params.get('view');
    if (
      deep === 'settings' ||
      deep === 'my' ||
      deep === 'library' ||
      deep === 'home' ||
      deep === 'games' ||
      deep === 'platform'
    ) {
      setView(deep);
    }

    return stopSync;
  }, [sessionUser, setView, toast, refreshConflicts, refreshRoms]);

  useEffect(() => {
    if (view === 'player' && !activeRom) exitPlayer();
  }, [view, activeRom, exitPlayer]);

  // Games 以外へ移ったら AI Novel の進行を捨てる（エミュレータ状態には触れない）
  useEffect(() => {
    if (view !== 'games') useNovelStore.getState().leaveGames();
  }, [view]);

  // PC / 横持ちの Home だけブラウザ文書スクロール。他画面はアプリ内スクロールに戻す。
  useEffect(() => {
    const root = document.documentElement;
    const on = view === 'home';
    root.classList.toggle('home-doc-scroll', on);
    if (!on) window.scrollTo(0, 0);
    return () => root.classList.remove('home-doc-scroll');
  }, [view]);

  // 設定 hydrate とセッション確認が終わるまで、ROM 起動と同じローダーを出す。
  // settingsReady 前に本体 UI を出すと、音量などが一瞬デフォルトになる。
  if (!sessionReady || !settingsReady) {
    return (
      <div className="relative h-full w-full bg-base-950">
        <StatusBarShield color="#000000" />
        <LoadProgressOverlay title={BRAND_LOGO} percent={bootPercent} label={bootLabel} />
      </div>
    );
  }

  if (!sessionUser) {
    return (
      <div className="h-full w-full overflow-hidden">
        <StatusBarShield />
        <LoginScreen onAuthed={(u) => setSessionUser(u)} />
        <Toasts />
      </div>
    );
  }

  const hub = (
    <AppShell active={view === 'settings' ? 'library' : isHubView(view) ? view : 'home'}>
      {view === 'home' ? (
        <HomeScreen />
      ) : view === 'my' ? (
        <MyScreen />
      ) : view === 'settings' ? (
        <SettingsScreen />
      ) : view === 'games' ? (
        <GamesScreen />
      ) : view === 'platform' ? (
        <ComingSoonScreen
          title="EmYu-Ba Platform"
          description="クリエイターがゲームを公開できるプラットフォームを準備しています。開発者向け機能は今後公開予定です。"
        />
      ) : (
        <LibraryScreen />
      )}
    </AppShell>
  );

  return (
    <div
      className={
        view === 'home'
          ? 'h-full w-full overflow-hidden md:h-auto md:min-h-[var(--app-height)] md:overflow-visible landscape:h-auto landscape:min-h-[var(--app-height)] landscape:overflow-visible'
          : 'h-full w-full overflow-hidden'
      }
    >
      <StatusBarShield color={view === 'player' || playingNovel ? '#000000' : '#07090f'} />
      {view === 'player' && activeRom ? (
        <PlayerScreen key={playGeneration} rom={activeRom} onExit={exitPlayer} />
      ) : playingNovel ? (
        <AiNovelPlayerScreen />
      ) : (
        hub
      )}

      <Toasts />

      {updateFn && (
        <div className="fixed inset-x-3 bottom-[max(5.5rem,calc(4.5rem+env(safe-area-inset-bottom)))] z-50 flex flex-col gap-2 rounded-2xl border border-amber-500/40 bg-base-800 p-3 shadow-xl sm:bottom-[max(1rem,env(safe-area-inset-bottom))] sm:left-auto sm:right-4 sm:w-96 landscape:bottom-[max(1rem,env(safe-area-inset-bottom))]">
          <p className="text-sm font-medium text-amber-100">アプリの更新があります</p>
          <p className="text-xs leading-relaxed text-slate-400">
            再読み込みだけでは古い版のまま残ることがあります。「更新」を押してください。
          </p>
          <div className="flex items-center justify-end gap-2">
            <button type="button" className="btn-ghost !min-h-0 !px-3 !py-1.5 !text-xs" onClick={() => setUpdateFn(null)}>
              後で
            </button>
            <button
              type="button"
              className="btn-primary !min-h-0 !px-3 !py-1.5 !text-xs"
              onClick={() => void updateFn()}
            >
              更新して再読み込み
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
